<template>
  <div class="sources-step">
    <h2 class="step-title">Deepen Your Mystic</h2>
    <p class="step-sub">Optional - share links or paste your story.</p>

    <div v-if="sources.length" class="source-inputs">
      <div v-for="(source, i) in sources" :key="i" class="source-row">
        <span class="source-icon">{{ sourceIcon(source.type) }}</span>
        <input
          v-model="source.url"
          :placeholder="sourcePlaceholder(source.type)"
          class="source-input"
          type="url"
          @focus="emit('context-signal', { panel: 'studio', view: 'sources_step', interaction: 'url_field_focused', data: { type: source.type } })"
        />
        <button class="remove-btn" @click="removeSource(i)">&times;</button>
      </div>
    </div>

    <div class="add-source-row">
      <button
        v-for="stype in addableTypes"
        :key="stype"
        class="add-chip"
        @click="addSource(stype)"
      >+ {{ typeLabel(stype) }}</button>
    </div>

    <div class="bio-section">
      <label class="bio-label">Or tell me more in your own words</label>
      <textarea
        v-model="bioText"
        class="bio-input"
        placeholder="Paste your bio, about page, or anything that sounds like you..."
        rows="4"
        @focus="emit('context-signal', { panel: 'studio', view: 'sources_step', interaction: 'bio_field_focused', data: {} })"
      ></textarea>
    </div>

    <div class="step-actions">
      <button
        class="primary-btn"
        :disabled="submitting"
        @click="submit"
      >
        {{ submitting ? 'Reading...' : 'Continue →' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import axios from 'axios'
import { static_store } from '@/store'

const props = defineProps({
  chosenTheme: { type: String, default: '' },
  chosenArchetype: { type: String, default: '' },
  interviewAnswers: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['submit', 'context-signal'])

const sources = ref([])
const bioText = ref('')
const submitting = ref(false)

const SOURCE_TYPES = ['website', 'instagram', 'tiktok', 'linkedin', 'facebook']

const addableTypes = computed(() =>
  SOURCE_TYPES.filter(t => !sources.value.find(s => s.type === t))
)

function sourceIcon(type) {
  const icons = { website: '🌐', instagram: '📸', tiktok: '🎵', linkedin: '💼', facebook: '📘' }
  return icons[type] || '🔗'
}

function typeLabel(type) {
  const labels = { website: 'Website', instagram: 'Instagram', tiktok: 'TikTok', linkedin: 'LinkedIn', facebook: 'Facebook' }
  return labels[type] || type
}

function sourcePlaceholder(type) {
  const ph = {
    website: 'https://yoursite.com',
    instagram: 'https://instagram.com/yourhandle',
    tiktok: 'https://tiktok.com/@you',
    linkedin: 'https://linkedin.com/in/you',
    facebook: 'https://facebook.com/yourpage',
  }
  return ph[type] || 'https://...'
}

function addSource(type) {
  sources.value.push({ type, url: '' })
}

function removeSource(i) {
  sources.value.splice(i, 1)
}

async function submit() {
  submitting.value = true
  try {
    const payload = {
      sources: [],
      chosen_theme: props.chosenTheme,
      chosen_archetype: props.chosenArchetype,
      interview_answers: props.interviewAnswers,
    }
    for (const s of sources.value) {
      if (s.url.trim()) {
        payload.sources.push({ type: s.type, url: s.url.trim() })
      }
    }
    if (bioText.value.trim()) {
      payload.sources.push({ type: 'bio_text', text: bioText.value.trim() })
    }

    const token = localStorage.getItem('authToken')
    const resp = await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/start/`,
      payload,
      { headers: { Authorization: `Token ${token}` }, timeout: 60000 }
    )
    emit('submit', { provisioningId: resp.data.provisioning_id })
  } catch (e) {
    // This step reproduces clean locally (200 in ~80ms, pipeline completes), so
    // a prod failure can only be diagnosed from the browser. Record the fields
    // that tell the causes apart — a 502 with no CORS headers, a real timeout,
    // and a request that never left all look identical from the alert alone.
    const timedOut = e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT'
    const noResponse = timedOut || e.message === 'Network Error' || !e.response
    console.error('[become-mystic/start] failed', {
      status: e.response?.status ?? null,
      code: e.code ?? null,
      message: e.message,
      body: e.response?.data ?? null,
      url: `${static_store.apiUrl}/reader/become-mystic/start/`,
      timedOut,
    })
    // A server that DID answer must say so — a 500/502 masquerading as "no
    // response" is what hid this in the first place.
    const msg = e.response?.data?.error
      || (e.response ? `The server returned ${e.response.status}. Please try again.` : null)
      || (noResponse ? 'That took a moment and didn’t go through - please try again.' : e.message)
    alert(msg)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.sources-step { color: #E8D8FF; }
.step-title {
  font-family: 'Cinzel', serif; font-size: 24px; font-weight: 600; color: #FFDFA3;
  margin: 0 0 8px; padding: 0; border: none; border-radius: 0;
  background: none; box-shadow: none; text-align: left;
}
.step-sub { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.65; margin-bottom: 24px; }

.source-inputs { display: flex; flex-direction: column; gap: 10px; margin-bottom: 16px; }
.source-row { display: flex; align-items: center; gap: 8px; }
.source-icon { font-size: 18px; width: 24px; text-align: center; flex-shrink: 0; }
.source-input { flex: 1; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 10px 12px; color: #E8D8FF; font-size: 13px; font-family: inherit; }
.source-input:focus { outline: none; border-color: rgba(125,232,204,0.4); }
.remove-btn { background: none; border: none; color: rgba(255,255,255,0.3); font-size: 18px; cursor: pointer; padding: 0 4px; flex-shrink: 0; }

.add-source-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 24px; }
.add-chip { background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1); border-radius: 20px; padding: 5px 14px; font-size: 12px; color: rgba(255,255,255,0.5); cursor: pointer; transition: all 0.2s; font-family: inherit; }
.add-chip:hover { border-color: rgba(125,232,204,0.3); color: #7DE8CC; }

.bio-section { margin-bottom: 24px; }
.bio-label { display: block; font-size: 12px; color: rgba(255,255,255,0.4); margin-bottom: 8px; }
.bio-input { width: 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 12px; color: #E8D8FF; font-size: 13px; font-family: inherit; resize: vertical; box-sizing: border-box; }
.bio-input:focus { outline: none; border-color: rgba(125,232,204,0.4); }

.step-actions { margin-top: 8px; }
.primary-btn { width: 100%; padding: 14px; background: rgba(50,192,158,0.12); border: 1px solid rgba(50,192,158,0.35); border-radius: 10px; color: #7DE8CC; font-size: 15px; font-weight: 500; cursor: pointer; font-family: inherit; transition: all 0.2s; }
.primary-btn:hover:not(:disabled) { background: rgba(50,192,158,0.22); }
.primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
</style>
